<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useUiStore } from '@/stores';
import RoleService from '@/services/api/RoleService';
import { toastServiceError } from '@/services/core/notification';
import { Permission, RoleModel } from '@/types';

// Composables
const route = useRoute();
const uiStore = useUiStore();

// State
const role = ref<RoleModel | undefined>(undefined);

// Methods
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchRole = async (id: number) => {
    setLoading(true);
    try {
        const response: AxiosResponse<RoleModel> = await RoleService.get(id);
        role.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

// Lifecycle
onMounted(() => {
    const id = Number(route.params.id);
    if (!isNaN(id)) fetchRole(id);
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('role.view.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListRole' }" />
                    <router-link
                        v-if="$can(Permission.EDIT_ROLE)"
                        :to="{ name: 'EditRoleById', params: { id: route.params.id } }"
                        class="block hover:bg-gray-50"
                    >
                        <UiButton variant="primary" text-variant="white">
                            {{ $t('core.actions.Edit') }}
                        </UiButton>
                    </router-link>
                </div>
            </template>
        </UiPageHeader>

        <div class="space-y-4">
            <UiPanel>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                        <h3 class="mb-1 font-semibold">
                            {{ $t('role.labels.name') }}
                        </h3>
                        <p>{{ role?.name }}</p>
                    </div>
                </div>
            </UiPanel>

            <UiPanel>
                <h3 class="mb-4 text-base font-semibold text-gray-800">
                    {{ $t('role.view.section.users.title') }}
                </h3>
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    <UiPanel
                        v-for="user in role?.users"
                        :key="user.id"
                        class="flex bg-white p-2"
                        no-padding
                    >
                        <div class="mr-2">
                            <UiGravatar
                                :email="user.email"
                                :name="user.name"
                                class="h-12 w-12 rounded-full ring-1 ring-gray-300 focus:ring-2 focus:ring-opacity-50"
                            />
                        </div>
                        <div>
                            <component
                                :is="$can(Permission.VIEW_USER) ? RouterLink : 'p'"
                                :to="{ name: 'ViewUserById', params: { id: user.id } }"
                                class="text-primary truncate text-sm font-medium"
                            >
                                {{ user.name }}
                            </component>
                            <p class="flex items-center text-sm text-gray-500">
                                <MailIcon
                                    class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                                    aria-hidden="true"
                                />
                                {{ user.email }}
                            </p>
                        </div>
                    </UiPanel>
                </div>
            </UiPanel>
        </div>
    </div>
</template>
